import type { WebhookIntegrationShortData } from '@shared/api';
import { useEffect, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex } from 'antd';
import styles from './integrations.module.scss';
import { Text } from '@shared/ui/antd';
import { IntegrationItem } from './ui/integrations-item/integrations-item';
import { useUpdateIntegration } from '@modules/integrations/hooks/use-update-integration';
import { ScrollContainer } from '@shared/ui/scroll-container';

interface IntegrationsProps {
  integrations?: WebhookIntegrationShortData[];
  onCreateIntegration: () => void;
  isCreateLoading: boolean;
  openIntegrationId?: number | null;
}

export const Integrations: FC<IntegrationsProps> = ({
  integrations,
  onCreateIntegration,
  isCreateLoading,
  openIntegrationId,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'integrations' });

  const [selectedIntegrationIdToSettingsUp, setSelectedIntegrationIdToSettingsUp] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (openIntegrationId !== undefined) {
      setSelectedIntegrationIdToSettingsUp(openIntegrationId ?? null);
    }
  }, [openIntegrationId]);

  const hasIntegrations = (integrations?.length ?? 0) > 0;

  const { updateIntegration, isLoading } = useUpdateIntegration();

  const handleToggle = (id: number) => {
    setSelectedIntegrationIdToSettingsUp((prev) => (prev === id ? null : id));
  };

  return (
    <ScrollContainer>
      <Flex vertical className={styles.root} gap={16}>
        {!hasIntegrations && (
          <Flex vertical gap={8} className={styles.intro}>
            <Text variant="md" fontWeight={700}>
              {t('title')}
            </Text>
            <Text variant="sm" className={styles.description}>
              {t('description')}
            </Text>
            <Button
              type="default"
              className={styles.addButton}
              onClick={onCreateIntegration}
              loading={isCreateLoading}
            >
              {t('add')}
            </Button>
          </Flex>
        )}

        {hasIntegrations && (
          <Flex vertical gap={12} className={styles.list}>
            {integrations!.map((integration) => (
              <IntegrationItem
                key={integration.id}
                integration={integration}
                isOpen={selectedIntegrationIdToSettingsUp === integration.id}
                isSubmitLoading={isLoading}
                onToggle={handleToggle}
                onSubmit={(data) => updateIntegration(integration.id, data)}
              />
            ))}

            <Button
              type="default"
              className={styles.addButton}
              onClick={onCreateIntegration}
              loading={isCreateLoading}
            >
              {t('add')}
            </Button>
          </Flex>
        )}
      </Flex>
    </ScrollContainer>
  );
};
